import React from "react";
import { motion } from "framer-motion";

/* Large "hero" surface — bigger radius/padding than the standard `.card`
 * (rounded-2xl), reserved for the one or two big-number/brand moments per
 * page (e.g. DashboardHome's top banner and closing positioning panel).
 *
 * `photo` puts a command-centre image behind the panel. The panel's content
 * is dark text on white, so the image cannot simply sit behind it — it is
 * pushed to the right third and veiled by a white gradient that stays fully
 * opaque under the copy and clears only where there is nothing to read. The
 * result is a photographic edge rather than a background image, which is the
 * only way to keep a white-card panel legible.
 *
 * Panels that pass no `photo` render exactly as before. */
export default function HeroPanel({
  children, className = "", initial, animate, photo, photoAlt = "",
}) {
  return (
    <motion.div
      initial={initial ?? { opacity: 0, y: 12 }}
      animate={animate ?? { opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-3xl bg-white border border-slate-200 shadow-card p-6 md:p-8 ${className}`}
    >
      {photo && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src={photo}
            alt={photoAlt}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          {/* White veil, lightened so the control room actually reads.
              It still holds near-opaque at the far left, where the heading and
              paragraph sit — dark slate text on an unveiled photo of a dark
              room is the one thing this panel cannot survive — then falls away
              quickly so the right two-thirds show the consoles clearly. */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-white/15" />
          {/* Softens the top and bottom edges so the photo does not cut hard
              against the panel border. Lighter than before to match. */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/45 via-transparent to-white/45" />
        </div>
      )}

      {/* Content sits above the photo layers. */}
      <div className="relative">{children}</div>
    </motion.div>
  );
}
